import { checkRequest } from "./modulePermissions";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://147.93.169.157:5000";
const API_VERSION = process.env.REACT_APP_API_VERSION || "1";

export const apiUrl = (path: string): string =>
  `${API_BASE_URL}/api/v${API_VERSION}${path}`;

const TOKEN_KEY = "ich-auth-token";
const TOKEN_EXPIRES_KEY = "ich-auth-token-expires";

export const getAuthToken = (): string | null =>
  localStorage.getItem(TOKEN_KEY);

export const getTokenExpiry = (): number | null => {
  const raw = localStorage.getItem(TOKEN_EXPIRES_KEY);
  if (!raw) return null;
  const ts = Date.parse(raw);
  return Number.isNaN(ts) ? null : ts;
};

export const isTokenValid = (): boolean => {
  const token = getAuthToken();
  if (!token) return false;
  const expiry = getTokenExpiry();
  if (expiry === null) return true;
  return Date.now() < expiry;
};

export const setAuthToken = (token: string, expiresAt: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRES_KEY, expiresAt);
};

export const clearAuthToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_KEY);
};

/** Fired when the API rejects this browser's login token (expired or no
 * longer valid), so the app can end the session. See AuthContext. */
export const SESSION_EXPIRED_EVENT = "ich:session-expired";

export const decodeJwt = (token: string): Record<string, any> => {
  try {
    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
};

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const parseErrorMessage = async (response: Response): Promise<string> => {
  try {
    const contentType = response.headers.get("content-type") || "";
    // ASP.NET Core's ProblemDetails responses (400/403/404/etc.) are served
    // as "application/problem+json", not "application/json" — that content
    // type doesn't contain the substring "application/json" (the "json"
    // comes after "+", not right after "application/"), so it used to fall
    // through to the plain-text branch below and surface the raw JSON blob
    // instead of the parsed `detail` field.
    if (contentType.includes("json")) {
      const body = await response.json();
      // ASP.NET Core's ValidationProblemDetails puts the actually-useful
      // per-field messages under `errors`, with a generic `title` like "One
      // or more validation errors occurred." — prefer the field messages.
      if (body?.errors && typeof body.errors === "object") {
        const fieldMessages = Object.entries(
          body.errors as Record<string, unknown>,
        ).flatMap(([field, messages]) =>
          Array.isArray(messages)
            ? messages.map((m) => `${field}: ${m}`)
            : [`${field}: ${messages}`],
        );
        if (fieldMessages.length > 0) return fieldMessages.join(" ");
      }
      // For a plain ProblemDetails error (no `errors`), `detail` usually has
      // more specific information than the generic `title` — e.g. a DB
      // error's message vs. just "An unexpected error occurred".
      return (
        body?.detail ||
        body?.message ||
        body?.title ||
        body?.error ||
        `Request failed (${response.status})`
      );
    }
    const text = await response.text();
    return text || `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
};

export const apiFetch = async (
  path: string,
  options: RequestInit = {},
  // skipAuth: never attach the stored token, even if one exists. For the
  // no-login guest app (GuestRoomServicePage) — a staff member's session
  // sitting in localStorage from an unrelated tab/device must not leak into
  // an anonymous guest's requests, since the backend would then authorize
  // (or reject) the call as THAT staff member instead of as the verified
  // guest it actually is.
  { skipAuth = false }: { skipAuth?: boolean } = {}
): Promise<Response> => {
  // Role permissions: a write the current page's module doesn't allow is
  // refused before it leaves the browser (see PermissionsContext). The guest
  // app's skipAuth calls act as the guest, not a staff role, so they skip it.
  const refusal = skipAuth ? null : checkRequest(options.method, path);
  if (refusal) throw new ApiError(refusal, 403);

  const token = skipAuth ? null : getAuthToken();
  const headers: HeadersInit = {
    // A FormData body (file uploads) must set its own multipart Content-Type,
    // boundary included, so the JSON default is left off for it.
    ...(options.body instanceof FormData
      ? {}
      : { "Content-Type": "application/json" }),
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let response: Response;
  try {
    response = await fetch(apiUrl(path), { ...options, headers });
  } catch {
    throw new ApiError(
      "Unable to reach the server. Check your connection and try again.",
      0
    );
  }

  if (!response.ok) {
    const message = await parseErrorMessage(response);
    const genericFailure = `Request failed (${response.status})`;
    let friendlyMessage = message;
    if (response.status === 401) {
      // "Invalid email or password." only makes sense for the login call
      // itself — every other endpoint hits this same 401 branch when the
      // stored token has simply expired, and showing a login-specific
      // message there ("Invalid email or password.") on e.g. a menu-item
      // save makes the failure look unrelated to auth, so it goes unnoticed
      // and the action silently never happened.
      friendlyMessage = path.startsWith("/auth/login")
        ? "Invalid email or password."
        : "Your session has expired. Please log in again.";
      // The API's token check marks its 401 with "WWW-Authenticate: Bearer";
      // a 401 a controller returns itself, such as a wrong current password,
      // doesn't. Only the former means this session is over.
      const challenge = response.headers.get("WWW-Authenticate") ?? "";
      if (!skipAuth && challenge.startsWith("Bearer")) {
        clearAuthToken();
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
    } else if (response.status === 403 && message === genericFailure) {
      // The API's branch fence refuses with an empty 403 — most often a
      // branch user changing an account-wide (no-branch) record, which is
      // read-only for them.
      friendlyMessage =
        "You don't have permission to change this record. Account-wide records can only be changed by a user with access to all branches.";
    } else if (response.status === 409 && message === genericFailure) {
      // The API often returns a 409 with no body — this is the common cause
      // (a unique field, e.g. email or account code, already exists).
      friendlyMessage =
        "This already exists — check for a duplicate email or code.";
    }
    throw new ApiError(friendlyMessage, response.status);
  }

  return response;
};
